/**
 * Chassis-page Alarms tab — consumes ``GET /api/chassis/{id}/alarms``
 * (SP3 backend).
 *
 * Surface:
 *   - Per-chassis bulk mute toggle (goal g).
 *   - Severity filter chips (critical / major / minor / warning / info).
 *   - One row per alarm with: severity pill, name, resource,
 *     raised-at, parsed_data preview (info-icon expand), ack +
 *     mute-1h buttons.
 *   - Acknowledged rows greyed out + ``ack by …`` suffix.
 *
 * Operator-driven mutations (ack / mute) invalidate the list +
 * navbar-count query so the bell badge updates within a refetch.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BellOff,
  Check,
  ChevronRight,
  Filter,
  TimerReset,
} from "lucide-react";
import { useMemo, useState } from "react";

import { api } from "../../../../api/client";
import { LocalTime } from "../../../../lib/datetime";
import { Badge } from "../../../components/Badge";
import { CopyableCell } from "../../../components/CopyableCell";
import { InfoExpandButton } from "../../../components/InfoExpandButton";
import { SeverityPill } from "../../../components/SeverityPill";
import { useToast } from "../../../components/Toast";

type Alarm = {
  id: number;
  chassis_id: number;
  job_id: string | null;
  alarm_name: string;
  severity: string;
  resource: string;
  raised_at: string;
  parsed_data: Record<string, unknown> | null;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  muted_until: string | null;
  cleared_at: string | null;
};

type AlarmListResponse = {
  alarms: Alarm[];
  total: number;
};

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  major: 1,
  minor: 2,
  warning: 3,
  info: 4,
};

function isMuted(a: Alarm): boolean {
  if (!a.muted_until) return false;
  return new Date(a.muted_until).getTime() > Date.now();
}

export function AlarmsTab({ chassisId }: { chassisId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);

  const q = useQuery<AlarmListResponse>({
    queryKey: ["v2", "chassis", chassisId, "alarms"],
    queryFn: async () => {
      const res = await api.get<AlarmListResponse>(
        `/chassis/${chassisId}/alarms`,
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["v2", "chassis", chassisId, "alarms"] });
    qc.invalidateQueries({ queryKey: ["v2", "chassis", chassisId, "alarms-count"] });
    qc.invalidateQueries({ queryKey: ["v2", "alarm-count"] });
  };

  const ack = useMutation({
    mutationFn: async (id: number) => api.post(`/alarms/${id}/ack`, {}),
    onSuccess: () => {
      invalidate();
      toast.push("Alarm acknowledged", "success");
    },
  });

  const mute1h = useMutation({
    mutationFn: async (id: number) =>
      api.post(`/alarms/${id}/mute`, { duration_seconds: 3600 }),
    onSuccess: () => {
      invalidate();
      toast.push("Muted for 1h", "success");
    },
  });

  const unmute = useMutation({
    mutationFn: async (id: number) =>
      api.post(`/alarms/${id}/mute`, { duration_seconds: 0 }),
    onSuccess: () => {
      invalidate();
      toast.push("Mute cleared", "success");
    },
  });

  const muteChassis1h = useMutation({
    mutationFn: async () =>
      api.post(`/chassis/${chassisId}/alarms/mute`, {
        duration_seconds: 3600,
      }),
    onSuccess: () => {
      invalidate();
      toast.push("All alarms muted for 1h", "success");
    },
  });

  const unmuteChassis = useMutation({
    mutationFn: async () =>
      api.post(`/chassis/${chassisId}/alarms/mute`, { duration_seconds: 0 }),
    onSuccess: () => {
      invalidate();
      toast.push("Chassis-wide mute lifted", "success");
    },
  });

  const visible = useMemo(() => {
    const rows = q.data?.alarms ?? [];
    const filtered = severityFilter
      ? rows.filter((r) => r.severity === severityFilter)
      : rows;
    return [...filtered].sort((a, b) => {
      // Active first, then by severity, then by raised_at desc.
      const aDone = !!a.acknowledged_at || !!a.cleared_at;
      const bDone = !!b.acknowledged_at || !!b.cleared_at;
      if (aDone !== bDone) return aDone ? 1 : -1;
      const aSev = SEVERITY_ORDER[a.severity] ?? 99;
      const bSev = SEVERITY_ORDER[b.severity] ?? 99;
      if (aSev !== bSev) return aSev - bSev;
      return b.raised_at.localeCompare(a.raised_at);
    });
  }, [q.data, severityFilter]);

  const severities = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of q.data?.alarms ?? []) {
      counts[a.severity] = (counts[a.severity] ?? 0) + 1;
    }
    return Object.entries(counts).sort(
      (a, b) => (SEVERITY_ORDER[a[0]] ?? 99) - (SEVERITY_ORDER[b[0]] ?? 99),
    );
  }, [q.data]);

  const total = q.data?.total ?? 0;
  const activeCount = (q.data?.alarms ?? []).filter(
    (a) => !a.acknowledged_at && !a.cleared_at && !isMuted(a),
  ).length;

  return (
    <section className="flex flex-col gap-3 rounded-lg bg-slate-900 shadow-border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Alarms
          </h2>
          <span className="text-xs text-slate-500">
            {activeCount} active · {total} total
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => muteChassis1h.mutate()}
            disabled={muteChassis1h.isPending}
            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-700 disabled:opacity-50"
            title="Mute every active alarm on this chassis for 1 hour"
          >
            <BellOff className="h-3 w-3" />
            Mute chassis 1h
          </button>
          <button
            type="button"
            onClick={() => unmuteChassis.mutate()}
            disabled={unmuteChassis.isPending}
            className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            title="Lift chassis-wide mute"
          >
            <TimerReset className="h-3 w-3" />
            Unmute
          </button>
        </div>
      </div>

      {severities.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <Filter className="h-3 w-3 text-slate-500" />
          <button
            type="button"
            onClick={() => setSeverityFilter(null)}
            className={[
              "rounded border px-1.5 py-0.5",
              severityFilter === null
                ? "border-emerald-700 bg-emerald-900/40 text-emerald-200"
                : "border-slate-700 text-slate-400 hover:bg-slate-800",
            ].join(" ")}
          >
            all
          </button>
          {severities.map(([sev, count]) => (
            <button
              key={sev}
              type="button"
              onClick={() => setSeverityFilter(sev === severityFilter ? null : sev)}
              className={[
                "rounded border px-1.5 py-0.5",
                sev === severityFilter
                  ? "border-emerald-700 bg-emerald-900/40 text-emerald-200"
                  : "border-slate-700 text-slate-400 hover:bg-slate-800",
              ].join(" ")}
            >
              {sev} · {count}
            </button>
          ))}
        </div>
      )}

      {q.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="text-xs text-slate-500">
          No alarms.{" "}
          {severityFilter
            ? "Try removing the severity filter."
            : "Run a parse_alarms job from the Inventory tab to refresh."}
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-6 px-2 py-1"></th>
              <th className="px-2 py-1">Severity</th>
              <th className="px-2 py-1">Alarm</th>
              <th className="px-2 py-1">Resource</th>
              <th className="px-2 py-1">Raised</th>
              <th className="px-2 py-1">State</th>
              <th className="px-2 py-1 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60 text-slate-300">
            {visible.map((a) => {
              const isOpen = expanded.has(a.id);
              const muted = isMuted(a);
              const isDone = !!a.acknowledged_at || !!a.cleared_at;
              return (
                <>
                  <tr
                    key={`row-${a.id}`}
                    className={isDone ? "opacity-50" : ""}
                  >
                    <td className="px-2 py-1.5 text-center">
                      <InfoExpandButton
                        expanded={isOpen}
                        label={`Expand alarm ${a.id}`}
                        onClick={() => {
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(a.id)) next.delete(a.id);
                            else next.add(a.id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <SeverityPill severity={a.severity} />
                    </td>
                    <td className="px-2 py-1.5">
                      <CopyableCell value={a.alarm_name} />
                    </td>
                    <td className="px-2 py-1.5">
                      <CopyableCell value={a.resource || null} />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                      <LocalTime iso={a.raised_at} />
                    </td>
                    <td className="px-2 py-1.5 text-[11px]">
                      {a.cleared_at ? (
                        <Badge tone="success">cleared</Badge>
                      ) : a.acknowledged_at ? (
                        <span className="text-slate-500">
                          ack by {a.acknowledged_by}
                        </span>
                      ) : muted ? (
                        <Badge tone="muted">muted</Badge>
                      ) : (
                        <Badge tone="warning">active</Badge>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!a.acknowledged_at && (
                          <button
                            type="button"
                            onClick={() => ack.mutate(a.id)}
                            disabled={ack.isPending}
                            className="inline-flex items-center gap-1 rounded border border-emerald-800 bg-emerald-900/40 px-1.5 py-0.5 text-[10px] text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-50"
                            title="Acknowledge this alarm"
                          >
                            <Check className="h-3 w-3" />
                            ack
                          </button>
                        )}
                        {!muted ? (
                          <button
                            type="button"
                            onClick={() => mute1h.mutate(a.id)}
                            disabled={mute1h.isPending}
                            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-700 disabled:opacity-50"
                            title="Mute this alarm for 1 hour"
                          >
                            <BellOff className="h-3 w-3" />
                            mute 1h
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => unmute.mutate(a.id)}
                            disabled={unmute.isPending}
                            className="inline-flex items-center gap-1 rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                            title="Lift mute on this alarm"
                          >
                            <TimerReset className="h-3 w-3" />
                            unmute
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`exp-${a.id}`} className="bg-slate-950/60">
                      <td></td>
                      <td colSpan={6} className="px-2 py-2">
                        <pre className="overflow-x-auto rounded bg-slate-950 p-2 font-mono text-[10px] text-slate-400">
                          {a.parsed_data
                            ? JSON.stringify(a.parsed_data, null, 2)
                            : "(no parsed_data — adapter didn't structure this alarm)"}
                        </pre>
                        {a.job_id && (
                          <p className="mt-1 text-[10px] text-slate-500">
                            Raised by job{" "}
                            <code className="font-mono">{a.job_id}</code>
                            {" — "}
                            <a
                              href={`/settings/jobs/${a.job_id}`}
                              className="text-emerald-400 hover:text-emerald-300"
                            >
                              open transcript
                            </a>
                          </p>
                        )}
                        {a.muted_until && muted && (
                          <p className="mt-1 text-[10px] text-amber-300">
                            <ChevronRight className="inline h-3 w-3" />
                            Muted until <LocalTime iso={a.muted_until} />
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
